import {
  Alert,
  AppBar,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Toolbar,
  Typography,
} from '@mui/material';
import { Add, ArrowBack, NotificationsNoneOutlined } from '@mui/icons-material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@/shared/components/notifications';
import { AlertListItem } from '../types';
import { disableAlert, getAlerts } from '../services/alertsService';
import { AlertCard } from './AlertCard';
import { MatchingOffersSection } from './MatchingOffersSection';

const PAGE_LIMIT = 3;

export function AlertsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();

  const [alerts, setAlerts] = useState<AlertListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [confirmDisableId, setConfirmDisableId] = useState<string | null>(null);
  const [disabling, setDisabling] = useState(false);

  const loadAlerts = useCallback(async (pageNum: number, append: boolean) => {
    try {
      if (pageNum === 1 && !append) {
        setLoading(true);
        setError(false);
      } else {
        setLoadingMore(true);
      }

      const result = await getAlerts(pageNum, PAGE_LIMIT);
      setAlerts(prev => (append ? [...prev, ...result.data] : result.data));
      setTotal(result.total);
      setPage(pageNum);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadAlerts(1, false);
  }, [loadAlerts]);

  const handleLoadMore = () => {
    loadAlerts(page + 1, true);
  };

  const handleEdit = (id: string) => {
    navigate(`/alerts/${id}/edit`);
  };

  const handleDisableClick = (id: string) => {
    setConfirmDisableId(id);
  };

  const handleDisableCancel = () => {
    setConfirmDisableId(null);
  };

  const handleDisableConfirm = async () => {
    if (!confirmDisableId) return;
    const id = confirmDisableId;
    setConfirmDisableId(null);
    setDisabling(true);
    try {
      await disableAlert(id);
      showSuccess(t('alerts.disable.success'));
      await loadAlerts(1, false);
    } catch {
      showError(t('alerts.disable.error'));
    } finally {
      setDisabling(false);
    }
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }} data-testid="alerts-page">
      {/* Header */}
      <AppBar
        position="fixed"
        sx={{
          bgcolor: 'primary.main',
          borderRadius: '0 0 16px 16px',
          zIndex: theme => theme.zIndex.appBar,
          paddingTop: 'env(safe-area-inset-top)',
        }}
        data-testid="alerts-header"
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/')}
            aria-label={t('common.back', 'Retour')}
            data-testid="alerts-back-button"
          >
            <ArrowBack />
          </IconButton>
          <Box
            sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'center', mr: 5 }}
          >
            <Typography variant="h6" component="div" sx={{ color: 'white', fontWeight: 600 }}>
              {t('alerts.title', 'Alertes')}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ px: 2, pt: 2, pb: 3 }}>
        {/* Create Alert button */}
        <Button
          fullWidth
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/alerts/create')}
          data-testid="alerts-create-button"
          aria-label={t('alerts.create.title', 'Créer une alerte')}
          sx={{
            bgcolor: 'secondary.main',
            color: 'secondary.contrastText',
            fontWeight: 700,
            borderRadius: 1,
            py: 1.5,
            mb: 2,
            '&:hover': { bgcolor: 'secondary.dark' },
          }}
        >
          {t('alerts.create.title', 'Créer une alerte')}
        </Button>

        {/* My Alerts card */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            p: 2,
            mb: 2,
          }}
        >
          {/* Section title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '16px',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <NotificationsNoneOutlined sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" fontWeight={700}>
              {t('alerts.title', 'Mes alertes')}
            </Typography>
          </Box>

          {/* Loading skeletons */}
          {loading && (
            <Box data-testid="alerts-loading">
              {[1, 2, 3].map(i => (
                <Box key={i} sx={{ mb: 2 }}>
                  <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2 }} />
                </Box>
              ))}
            </Box>
          )}

          {/* Error state */}
          {!loading && error && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => loadAlerts(1, false)}>
                  {t('alerts.list.retry', 'Réessayer')}
                </Button>
              }
              data-testid="alerts-error"
            >
              {t('alerts.list.error', 'Impossible de charger les alertes. Veuillez réessayer.')}
            </Alert>
          )}

          {/* Empty state */}
          {!loading && !error && alerts.length === 0 && (
            <Box sx={{ textAlign: 'center', mt: 4, mb: 2 }} data-testid="alerts-empty-message">
              <Typography variant="body1" color="text.secondary">
                {t('alerts.empty', "Vous n'avez pas encore d'alertes. Créez-en une !")}
              </Typography>
            </Box>
          )}

          {/* Alert list */}
          {!loading && !error && alerts.length > 0 && (
            <Box data-testid="alerts-list">
              {alerts.map(alert => (
                <AlertCard
                  key={alert.id}
                  alert={alert}
                  onEdit={handleEdit}
                  onDisable={handleDisableClick}
                />
              ))}

              {/* See more button */}
              {alerts.length < total && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={handleLoadMore}
                    disabled={loadingMore || disabling}
                    data-testid="alerts-see-more-button"
                    sx={{ textTransform: 'none', borderRadius: 3, px: 4 }}
                  >
                    {loadingMore ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                    {loadingMore
                      ? t('alerts.list.loadingMore', 'Chargement...')
                      : t('alerts.list.seeMore', 'Voir plus')}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Container>

      {/* Products matching alerts */}
      <Container maxWidth="md" sx={{ px: 2, pb: 4 }}>
        <MatchingOffersSection hasActiveAlerts={!loading && !error && alerts.length > 0} />
      </Container>

      {/* Disable confirmation dialog */}
      <Dialog
        open={confirmDisableId !== null}
        onClose={handleDisableCancel}
        data-testid="alerts-disable-dialog"
      >
        <DialogTitle>{t('alerts.disable.confirm.title', "Désactiver l'alerte")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'alerts.disable.confirm.message',
              'Êtes-vous sûr de vouloir désactiver cette alerte ?'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDisableCancel} data-testid="alerts-disable-cancel">
            {t('alerts.disable.confirm.cancel', 'Annuler')}
          </Button>
          <Button
            onClick={handleDisableConfirm}
            color="error"
            variant="contained"
            data-testid="alerts-disable-confirm"
          >
            {t('alerts.disable.confirm.ok', 'Désactiver')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
